import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Activity, Home, Star, Pill, ClipboardList, Settings, 
  Folder, Smartphone, Key, LogOut, Stethoscope, Truck, Plane, Mountain, Shield,
  ChevronDown, ChevronRight, Brain, Moon, Printer, Clock, Trash2, FlaskConical, Heart, Phone, ClipboardPlus, Info, WifiOff, ShieldCheck, Loader2, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API, LOCAL_APP_VERSION } from "@/config/constants";
import { useAuth } from "@/contexts/AuthContext";
import { usePWA } from "@/contexts/PWAContext";
import { useService } from "@/contexts/ServiceContext";
import ReportErrorDialog from "@/components/ReportErrorDialog";
import { ChangePasswordDialog } from "@/components/auth";
import OfflineVideosDialog from "@/components/OfflineVideosDialog";
import OfflineReadinessDialog from "@/components/OfflineReadinessDialog";
import WhatsNewDialog from "@/components/WhatsNewDialog";

// Icon mapping for categories
const iconMap = {
  Activity, Home, Star, Pill, ClipboardList, Settings, Folder
};

const SERVICE_ICONS = {
  ground: Truck,
  sfa_emr: Shield,
  air_ambulance: Plane,
  wes: Mountain,
};

const Sidebar = ({ categories, currentCategory, onClose, iconMap: externalIconMap, recentHistory = [], clearHistory }) => {
  const navigate = useNavigate();
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWA();
  const { user, logout, isAdmin, isEditor, canAdminUI } = useAuth();
  const { activeService, switchService, SERVICES } = useService();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showOfflineVideos, setShowOfflineVideos] = useState(false);
  const [showOfflineReadiness, setShowOfflineReadiness] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [redMode, setRedMode] = useState(() => localStorage.getItem("ems_red_mode") === "true");
  const [brightness, setBrightness] = useState(() => parseInt(localStorage.getItem("ems_brightness") || "100", 10));

  const toggleRedMode = useCallback(() => {
    setRedMode(prev => {
      const next = !prev;
      if (next) {
        document.documentElement.classList.add("red-mode");
        document.documentElement.style.setProperty("--night-brightness", `${brightness}%`);
        localStorage.setItem("ems_red_mode", "true");
      } else {
        document.documentElement.classList.remove("red-mode");
        document.documentElement.style.removeProperty("--night-brightness");
        localStorage.setItem("ems_red_mode", "false");
      }
      return next;
    });
  }, [brightness]);

  const handleBrightness = useCallback((val) => {
    setBrightness(val);
    localStorage.setItem("ems_brightness", String(val));
    document.documentElement.style.setProperty("--night-brightness", `${val}%`);
  }, []);

  // Manual "Check for updates" — fetches latest version, compares to local,
  // clears all caches and reloads if a new build is available.
  const checkForUpdates = useCallback(async () => {
    if (checkingUpdate) return;
    if (!navigator.onLine) {
      toast.error("You're offline — reconnect to check for updates");
      return;
    }
    setCheckingUpdate(true);
    try {
      const res = await axios.get(`${API}/version`, {
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      const remote = res.data?.version;
      if (!remote) {
        toast.error("Could not read server version");
        return;
      }
      if (remote === LOCAL_APP_VERSION) {
        toast.success(`You're on the latest version (v${LOCAL_APP_VERSION})`);
        return;
      }
      // New build available — clear caches + unregister SW + hard reload
      toast(`Update available — installing v${remote}…`, { icon: "🚀" });
      try {
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ("caches" in window) {
          const names = await caches.keys();
          // Preserve the offline videos cache so users don't lose downloaded videos
          await Promise.all(
            names.filter((n) => !n.includes("yems-videos")).map((n) => caches.delete(n))
          );
        }
      } catch {
        // Best-effort — proceed to reload regardless
      }
      setTimeout(() => window.location.reload(), 600);
    } catch {
      toast.error("Update check failed — try again in a moment");
    } finally {
      setCheckingUpdate(false);
    }
  }, [checkingUpdate]);

  useEffect(() => {
    if (redMode) {
      document.documentElement.classList.add("red-mode");
      document.documentElement.style.setProperty("--night-brightness", `${brightness}%`);
    }
  }, [redMode, brightness]);
  
  const icons = externalIconMap || iconMap;

  // Filter categories by active service
  const filteredCategories = categories.filter(cat => {
    const catService = cat.service || "ground";
    return catService === activeService;
  });
  
  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }
    const installed = await promptInstall();
    if (installed) {
      toast.success("App installed successfully!");
    }
  };

  const handleLogout = () => {
    logout();
    onClose();
    navigate("/login");
    toast.success("Logged out successfully");
  };

  const handleServiceSwitch = (serviceId) => {
    switchService(serviceId);
    navigate("/");
    onClose();
  };
  
  return (
    <div className="h-full flex flex-col bg-card">
      <div className="p-4 border-b border-border">
        <Link to="/select-service" onClick={onClose} className="flex items-center gap-2">
          <img src="/yems-logo.png" alt="YEMS" className="w-8 h-8 rounded-lg object-contain" />
          <span className="font-heading text-xl font-bold">YEMS Clinical Guidelines</span>
        </Link>
      </div>
      
      <ScrollArea className="flex-1 p-2">
        <div className="space-y-1">
          <Link to="/" onClick={onClose}>
            <Button 
              variant={!currentCategory ? "secondary" : "ghost"} 
              className="w-full justify-start gap-2"
              data-testid="nav-home"
            >
              <Home className="w-4 h-4" />
              Dashboard
            </Button>
          </Link>
          
          {/* Service Selector - always visible near top */}
          <div className="pt-2 pb-1 px-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Service</span>
          </div>

          <div className="space-y-0.5 px-1 mb-2">
            {Object.entries(SERVICES).filter(([id]) => id !== "air_ambulance").map(([id, svc]) => {
              const Icon = SERVICE_ICONS[id] || Folder;
              const isActive = activeService === id;
              return (
                <Button
                  key={id}
                  variant={isActive ? "secondary" : "ghost"}
                  size="sm"
                  className="w-full justify-start gap-2 text-xs h-8"
                  onClick={() => handleServiceSwitch(id)}
                  data-testid={`service-${id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {svc.label}
                </Button>
              );
            })}
            <Button
              variant={activeService === "air_adult" ? "secondary" : "ghost"}
              size="sm"
              className="w-full justify-start gap-2 text-xs h-8"
              onClick={() => handleServiceSwitch("air_adult")}
              data-testid="service-air_adult"
            >
              <Plane className="w-3.5 h-3.5" />
              Air Ambulance — Adult
            </Button>
            <Button
              variant={activeService === "air_pediatric" ? "secondary" : "ghost"}
              size="sm"
              className="w-full justify-start gap-2 text-xs h-8"
              onClick={() => handleServiceSwitch("air_pediatric")}
              data-testid="service-air_pediatric"
            >
              <Plane className="w-3.5 h-3.5" />
              Air — OB/NEO/PEDS
            </Button>
          </div>

          <Link to="/favorites" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-favorites">
              <Star className="w-4 h-4" />
              Favorites
            </Button>
          </Link>
          
          <Link to="/medications" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-medications">
              <Pill className="w-4 h-4" />
              Medications
            </Button>
          </Link>
          
          <Link to="/procedures" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-procedures">
              <ClipboardList className="w-4 h-4" />
              Procedures
            </Button>
          </Link>

          <Link to="/phone-book" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-phone-book">
              <Phone className="w-4 h-4" />
              Phone Book
            </Button>
          </Link>

          <Link to="/interventions" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2 text-emerald-400" data-testid="nav-interventions">
              <ClipboardPlus className="w-4 h-4" />
              Intervention Log
            </Button>
          </Link>

          <Link to="/code-timer" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2 text-red-400" data-testid="nav-code-timer">
              <Heart className="w-4 h-4" />
              Code Timer
            </Button>
          </Link>

          <Link to="/calculator" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-drug-calculator">
              <FlaskConical className="w-4 h-4" />
              Drug Calculator
            </Button>
          </Link>

          <Link to="/quiz" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-quiz">
              <Brain className="w-4 h-4" />
              Quiz Mode
            </Button>
          </Link>

          <Link to="/reference-cards" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-reference-cards">
              <Printer className="w-4 h-4" />
              Reference Cards
            </Button>
          </Link>

          <Link to="/about" onClick={onClose}>
            <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-about">
              <Info className="w-4 h-4" />
              About / Disclaimer
            </Button>
          </Link>

          <div className="space-y-1 px-1">
            <Button
              variant="ghost"
              className={`w-full justify-start gap-2 ${redMode ? "text-red-400" : ""}`}
              onClick={toggleRedMode}
              data-testid="night-ops-toggle"
            >
              <Moon className="w-4 h-4" />
              Night Ops {redMode ? "ON" : "OFF"}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={checkForUpdates}
              disabled={checkingUpdate}
              data-testid="sidebar-check-updates-btn"
            >
              {checkingUpdate ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {checkingUpdate ? "Checking…" : "Check for Updates"}
              <Badge variant="outline" className="ml-auto text-[9px] font-mono px-1.5 py-0">v{LOCAL_APP_VERSION}</Badge>
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={() => setShowWhatsNew(true)}
              data-testid="sidebar-whats-new-btn"
            >
              <Sparkles className="w-4 h-4 text-cyan-400" />
              What's New
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={() => setShowOfflineReadiness(true)}
              data-testid="sidebar-offline-readiness-btn"
            >
              <ShieldCheck className="w-4 h-4" />
              Offline Readiness
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={() => setShowOfflineVideos(true)}
              data-testid="sidebar-offline-videos-btn"
            >
              <WifiOff className="w-4 h-4" />
              Offline Videos
            </Button>
            {redMode && (
              <div className="flex items-center gap-2 px-3 pb-1">
                <Moon className="w-3 h-3 text-red-500/60 shrink-0" />
                <input
                  type="range"
                  min="30"
                  max="100"
                  value={brightness}
                  onChange={(e) => handleBrightness(Number(e.target.value))}
                  data-testid="brightness-slider"
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-red-600"
                  style={{ background: `linear-gradient(to right, hsl(0 40% 20%) 0%, hsl(0 50% 45%) ${((brightness - 30) / 70) * 100}%, hsl(0 15% 12%) ${((brightness - 30) / 70) * 100}%)` }}
                />
                <span className="text-[10px] text-red-400/60 w-7 text-right shrink-0">{brightness}%</span>
              </div>
            )}
          </div>
          
          {(canAdminUI || isEditor) && (
            <Link to="/admin/dashboard" onClick={onClose}>
              <Button variant="ghost" className="w-full justify-start gap-2" data-testid="nav-admin">
                <Settings className="w-4 h-4" />
                Admin
              </Button>
            </Link>
          )}
          
          <div className="pt-4 pb-2 px-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Categories</span>
          </div>
          
          {filteredCategories.map((cat) => {
            const Icon = icons[cat.icon] || Folder;
            return (
              <Link key={cat.id} to={`/category/${cat.id}`} onClick={onClose}>
                <Button 
                  variant={currentCategory === cat.id ? "secondary" : "ghost"} 
                  className="w-full justify-start gap-2"
                  data-testid={`nav-category-${cat.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1 text-left">{cat.name}</span>
                  {cat.protocol_count > 0 && (
                    <Badge variant="outline" className="ml-auto">{cat.protocol_count}</Badge>
                  )}
                </Button>
              </Link>
            );
          })}

          {/* Recent History */}
          {recentHistory.length > 0 && (
            <>
              <div className="pt-4 pb-2 px-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recent</span>
                <button onClick={clearHistory} className="text-muted-foreground hover:text-foreground" data-testid="clear-history">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {recentHistory.slice(0, 5).map((item) => (
                <Link key={item.id} to={`/protocol/${item.id}`} onClick={onClose}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs h-7"
                    data-testid={`recent-${item.id}`}
                  >
                    <Clock className="w-3 h-3 shrink-0 text-muted-foreground" />
                    <span className="flex-1 text-left truncate">{item.name}</span>
                  </Button>
                </Link>
              ))}
            </>
          )}
        </div>
      </ScrollArea>
      
      {/* Bottom section */}
      <div className="p-2 border-t border-border space-y-2">
        {canInstall && !isInstalled && (
          <Button 
            variant="outline" 
            className="w-full justify-start gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            onClick={handleInstallClick}
            data-testid="install-app-button"
          >
            <Smartphone className="w-4 h-4" />
            Install App
          </Button>
        )}
        
        {showIOSInstructions && (
          <Card className="mb-2">
            <CardContent className="p-3 text-sm">
              <p className="font-medium mb-2">To install on iOS:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Tap the Share button</li>
                <li>Scroll down and tap "Add to Home Screen"</li>
                <li>Tap "Add"</li>
              </ol>
              <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setShowIOSInstructions(false)}>
                Got it
              </Button>
            </CardContent>
          </Card>
        )}
        
        {user && (
          <div className="space-y-2 px-2 py-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name || user.email}</p>
                <p className="text-xs text-muted-foreground truncate">{user.role}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <ReportErrorDialog />
            </div>
            <div className="flex gap-1">
              <Button 
                variant="ghost" size="sm" className="flex-1 justify-start gap-2"
                onClick={() => setShowChangePassword(true)}
                data-testid="change-password-button"
              >
                <Key className="w-4 h-4" />
                Change Password
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        
        <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
        <OfflineVideosDialog open={showOfflineVideos} onOpenChange={setShowOfflineVideos} />
        <OfflineReadinessDialog open={showOfflineReadiness} onOpenChange={setShowOfflineReadiness} />
        <WhatsNewDialog open={showWhatsNew} onOpenChange={setShowWhatsNew} />
      </div>
    </div>
  );
};

export default Sidebar;
